// health-20261104: connection health popover, opened from the footer lamp.
// Data o—o Droplet o—o Frontend o—o You. Polls /health every 3s while open.
// Until the server route carries feeds/host, Data + Droplet stats stay grey.
import { useEffect, useState } from 'react'
import ChartMenu from './menu/ChartMenu'
import { dbg } from '../utils/debugFlag'

const BASE = import.meta.env.VITE_HTTP_PROXY_BASE || 'https://history.cheddarterminal.app'
const C = { g: '#22c55e', y: '#f59e0b', r: '#ef4444', u: '#6b7280' }
const RANK = { u: 0, g: 1, y: 2, r: 3 }
const worst = (a) => a.reduce((w, c) => (RANK[c] > RANK[w] ? c : w), 'u')
const ageCol = (s, up) => (up === false ? 'r' : s == null ? 'u' : s < 15 ? 'g' : s < 120 ? 'y' : 'r')
const fmtAge = (s) => (s == null ? '—' : s < 60 ? `${Math.round(s)}s` : s < 3600 ? `${Math.round(s / 60)}m` : `${Math.round(s / 3600)}h`)
const FEEDS = [['sol', 'Solana'], ['cme', 'CME'], ['hl', 'HL'], ['stk', 'Schwab']]

function Node({ label, col, sub, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 62 }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: C[col], boxShadow: col === 'u' ? 'none' : `0 0 5px ${C[col]}` }} />
      <div style={{ marginTop: 4, fontWeight: 600 }}>{label}</div>
      <div style={{ opacity: 0.75, fontSize: '0.9em', whiteSpace: 'nowrap' }}>{sub}</div>
      {children}
    </div>
  )
}
const Wire = () => <div style={{ flex: 1, height: 1, minWidth: 14, background: 'currentColor', opacity: 0.3, marginTop: 5 }} />

export default function HealthPanel({ data, anchor, onClose }) {
  const [h, setH] = useState({ ok: null })
  const [, tick] = useState(0)
  useEffect(() => {
    let dead = false
    const poll = async () => {
      const t0 = performance.now()
      try {
        const ctl = new AbortController(); const to = setTimeout(() => ctl.abort(), 5000)
        const j = await fetch(`${BASE}/health`, { cache: 'no-store', signal: ctl.signal }).then(r => r.json())
        clearTimeout(to)
        if (!dead) setH({ ok: true, rtt: performance.now() - t0, j })
      } catch (e) {
        dbg('[health] poll failed', e?.message || e)
        if (!dead) setH({ ok: false })
      }
      if (!dead) tick(n => n + 1)
    }
    poll(); const t = setInterval(poll, 3000)
    return () => { dead = true; clearInterval(t) }
  }, [])

  // Data
  const feeds = h.j?.feeds
  const fCol = feeds ? FEEDS.map(([k]) => ageCol(feeds[k]?.age, feeds[k]?.up)) : []
  const dataCol = feeds ? worst(fCol) : 'u'
  // Droplet
  const host = h.j?.host
  const cpu = host?.load ? Math.round(100 * host.load[0] / (host.cpus || 1)) : null
  const dropParts = !host ? [] : [[cpu > 300 ? 'r' : cpu > 225 ? 'y' : 'g', 'cpu'], [host.mem_pct > 92 ? 'r' : host.mem_pct > 85 ? 'y' : 'g', 'ram'], [host.disk_pct > 95 ? 'r' : host.disk_pct > 85 ? 'y' : 'g', 'disk']]
  const dropWhy = dropParts.filter(x => x[0] === 'r' || x[0] === 'y').map(x => x[1] + (x[0] === 'r' ? '!' : '')).join(' ')
  const dropCol = h.ok === false ? 'r' : !host ? 'u'
    : worst(dropParts.map(x => x[0]))
  const dropSub = h.ok === false ? 'no reply' : h.ok == null ? '…' : !host ? 'up · stats n/a'
    : `cpu ${cpu}% · ram ${Math.round(host.mem_pct)}% · disk ${Math.round(host.disk_pct)}%${dropWhy ? ' <- ' + dropWhy : ''}`
  // Frontend (main socket, what the client already knows)
  const ws = data?.wsRef?.current
  const open = ws?.readyState === 1
  const msgAge = data?.lastWsMsgAtRef ? (Date.now() - data.lastWsMsgAtRef.current) / 1000 : null
  const lat = data?.appClockRef?.current?.ok ? data.appClockRef.current.delay : null
  const feCol = !ws ? 'u' : !open ? 'r' : msgAge > 90 ? 'r' : msgAge > 40 ? 'y' : 'g'
  const feSub = !ws ? 'no socket' : !open ? (['connecting', 'open', 'closing', 'closed'][ws.readyState]) : `${lat != null ? Math.round(lat) + 'ms · ' : ''}msg ${fmtAge(msgAge)}`
  // You
  const youCol = h.rtt == null ? (h.ok === false ? 'r' : 'u') : h.rtt < 300 ? 'g' : h.rtt < 1000 ? 'y' : 'r'

  return (
    <ChartMenu anchor={anchor} onClose={onClose} title="Connection health" closeButton width={360}>
      <div style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 4px', fontSize: 11 }}>
        <Node label="Data" col={dataCol} sub={feeds ? '' : 'unknown'}>
          {feeds && FEEDS.map(([k, n], i) => (
            <div key={k} style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: '0.9em', whiteSpace: 'nowrap' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: C[fCol[i]] }} />
              {n} {feeds[k]?.up === false ? 'down' : fmtAge(feeds[k]?.age)}
            </div>
          ))}
        </Node>
        <Wire />
        <Node label="Droplet" col={dropCol} sub={dropSub.split(' · ')[0]}>
          {host && dropSub.split(' · ').slice(1).map(s => <div key={s} style={{ opacity: 0.75, fontSize: '0.9em' }}>{s}</div>)}
        </Node>
        <Wire />
        <Node label="Frontend" col={feCol} sub={feSub} />
        <Wire />
        <Node label="You" col={youCol} sub={h.rtt != null ? `${Math.round(h.rtt)}ms` : h.ok === false ? 'no reply' : '…'} />
      </div>
    </ChartMenu>
  )
}
